import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { WorkOrderLineDto, FarmerAllocationDto } from '../../../services/api';
import type { PackagingContainer } from '../../../types/loading-plan';
import { Button } from '../../../components/ui/button';

interface Props {
  /** Allocations del palet en cuestión, ya filtradas por pallet.id. */
  allocations: Array<FarmerAllocationDto & { workOrderLineId: string; productName: string }>;
  orderLines: WorkOrderLineDto[];
  containers: PackagingContainer[];
  deadWeightKg: number;
  onClose: () => void;
}

/**
 * Panel informativo con los pesos de los envases que el mozo ha asignado a
 * este palet, y un resumen para verificar el peso de báscula:
 *
 *   - peso registrado (lo que el mozo metió por caja, sea bruto o neto)
 *   - peso de envases (tara × bultos) — solo si el mozo trabaja con bruto
 *   - peso muerto del palet (madera/plásticos/separadores)
 *   - total esperado en báscula
 *
 * No hace conversiones automáticas: solo expone los números para que el mozo
 * pueda contrastar a ojo con lo que marque la báscula.
 */
export const PackagingWeightsPanel: React.FC<Props> = ({
  allocations,
  orderLines,
  containers,
  deadWeightKg,
  onClose,
}) => {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const containerById = useMemo(() => {
    const map = new Map<string, PackagingContainer>();
    for (const c of containers) map.set(c.id, c);
    return map;
  }, [containers]);

  // Filas agregadas por (línea + envase): suma de bultos y peso registrado.
  const rows = useMemo(() => {
    type Row = {
      key: string;
      productName: string;
      packagingName: string;
      tareKg: number;
      bultos: number;
      registeredKg: number;
    };
    const byKey = new Map<string, Row>();
    for (const a of allocations) {
      const line = orderLines.find(l => l.id === a.workOrderLineId);
      const containerId = line?.packagingContainerId ?? null;
      const container = containerId ? containerById.get(containerId) : undefined;
      const key = `${a.workOrderLineId}::${containerId ?? 'none'}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.bultos += a.bultos;
        existing.registeredKg += a.quantity;
      } else {
        byKey.set(key, {
          key,
          productName: a.productName,
          packagingName: container?.name ?? line?.packagingContainerName ?? '—',
          tareKg: container?.tareWeightKg ?? 0,
          bultos: a.bultos,
          registeredKg: a.quantity,
        });
      }
    }
    return Array.from(byKey.values());
  }, [allocations, orderLines, containerById]);

  const totals = useMemo(() => {
    const bultos = rows.reduce((s, r) => s + r.bultos, 0);
    const tara = rows.reduce((s, r) => s + r.bultos * r.tareKg, 0);
    const registered = rows.reduce((s, r) => s + r.registeredKg, 0);
    const netIfGross = registered - tara;
    const totalIfGross = registered + deadWeightKg;
    const totalIfNet = registered + tara + deadWeightKg;
    return { bultos, tara, registered, netIfGross, totalIfGross, totalIfNet };
  }, [rows, deadWeightKg]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-[34rem] h-full bg-white shadow-xl flex flex-col">
        <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-xl font-bold text-slate-900">Pesos de envases</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Cerrar">
            <X size={20} />
          </button>
        </header>

        <div className="p-4 space-y-5 flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No hay cajas asignadas a este palet todavía.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left font-medium py-1.5">Producto</th>
                  <th className="text-left font-medium py-1.5">Envase</th>
                  <th className="text-right font-medium py-1.5">Tara/caja</th>
                  <th className="text-right font-medium py-1.5">Bultos</th>
                  <th className="text-right font-medium py-1.5">Tara total</th>
                  <th className="text-right font-medium py-1.5">Registrado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.key} className="border-b border-slate-100">
                    <td className="py-1.5 font-medium text-slate-800 uppercase">{r.productName}</td>
                    <td className="py-1.5 text-slate-600">{r.packagingName}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.tareKg.toFixed(2)} kg</td>
                    <td className="py-1.5 text-right tabular-nums">{r.bultos}</td>
                    <td className="py-1.5 text-right tabular-nums font-medium">{(r.bultos * r.tareKg).toFixed(2)} kg</td>
                    <td className="py-1.5 text-right tabular-nums font-medium">{r.registeredKg.toFixed(2)} kg</td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td className="py-2" colSpan={3}>Totales</td>
                  <td className="py-2 text-right tabular-nums">{totals.bultos}</td>
                  <td className="py-2 text-right tabular-nums">{totals.tara.toFixed(2)} kg</td>
                  <td className="py-2 text-right tabular-nums">{totals.registered.toFixed(2)} kg</td>
                </tr>
              </tbody>
            </table>
          )}

          {rows.length > 0 && (
            <section className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
              <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Resumen de pesos</h4>
              <p className="text-xs text-slate-500">
                Según cómo introduzcas los kilos (bruto o neto), interpreta el total esperado en báscula con la fila correspondiente.
              </p>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="space-y-1 bg-white rounded p-2 border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Si los pesos son BRUTOS</p>
                  <div className="flex justify-between">
                    <span>Registrado (con envase)</span>
                    <span className="tabular-nums font-medium">{totals.registered.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>− Tara envases</span>
                    <span className="tabular-nums">{totals.tara.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-1">
                    <span>Mercancía neta</span>
                    <span className="tabular-nums font-medium">{totals.netIfGross.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>+ Peso muerto</span>
                    <span className="tabular-nums">{deadWeightKg.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between border-t-2 border-slate-300 pt-1 font-bold text-base">
                    <span>Total báscula</span>
                    <span className="tabular-nums">{totals.totalIfGross.toFixed(2)} kg</span>
                  </div>
                </div>

                <div className="space-y-1 bg-white rounded p-2 border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Si los pesos son NETOS</p>
                  <div className="flex justify-between">
                    <span>Registrado (sin envase)</span>
                    <span className="tabular-nums font-medium">{totals.registered.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>+ Tara envases</span>
                    <span className="tabular-nums">{totals.tara.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>+ Peso muerto</span>
                    <span className="tabular-nums">{deadWeightKg.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between border-t-2 border-slate-300 pt-1 font-bold text-base">
                    <span>Total báscula</span>
                    <span className="tabular-nums">{totals.totalIfNet.toFixed(2)} kg</span>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        <footer className="p-4 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} className="w-full">Cerrar (Esc)</Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};
